import { confirmApp } from '../common/appDialog';
import React, { useState, useEffect, useRef } from 'react';
import UnifiedMap from './UnifiedMap';
import { normalizeISO3 } from '../utils/geography';
import { COUNTRY_CODES } from '../common/data';

// ✅ 1. Import your initialized instances from your config
import { db, auth } from '../../firebaseConfig';
import { getDoc, doc } from 'firebase/firestore'; 
import { sanitizeFirestoreData } from './reports/ReportShared';


// ✅ 2. Import Firestore methods and types directly from the SDK (Tree-shakable)
import { 
    collection, 
    query, 
    onSnapshot, 
    orderBy, 
    limit,
    where,
    addDoc,
    serverTimestamp 
} from 'firebase/firestore';

// ✅ 3. Import Auth methods from the Auth SDK (Fixes 'onAuthStateChanged' if used)
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

// ✅ 4. Import types from your central types file
import type { CountryRiskData, GeopoliticalEvent, RiskEvent, Page, AcapsCrisis } from '@types';

import useGoogleMaps from './useGoogleMaps'; 
import TerminologyInline from './TerminologyInline';

// ✅ ONLY ONE INTERFACE HERE (Delete the other two)
const DEFAULT_VIEW_STATE = { longitude: 0, latitude: 20, zoom: 2 };

export interface IntegratedThreatAnalysisProps {
    db: any; // 👈 Add this
    onBack: () => void;
    onNavigate: (nav: { page: Page; params?: any }) => void;
    onOpenGlossary?: (term: string) => void;
    riskData?: CountryRiskData[]; 
    alerts?: (GeopoliticalEvent | AcapsCrisis)[];
    params?: any;
    srmProject?: any;
    srmModule?: any;
}
export const IntegratedThreatAnalysis: React.FC<IntegratedThreatAnalysisProps> = ({ 
        db, // 👈 Add this
        onBack, 
        onNavigate, 
        onOpenGlossary,
        riskData = [], 
        alerts = [],
        params = {},
        srmProject,
        srmModule
    }) => {
    // 1. Unified Hub States
    
    const [newsEvents, setNewsEvents] = useState<RiskEvent[]>([]);
    const { isLoaded } = useGoogleMaps(); 
    const [user, setUser] = useState<any>(null);
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [threatGroups, setThreatGroups] = useState<string[]>([]);
    const [selectedThreatGroup, setSelectedThreatGroup] = useState('');
    const [isLoadingThreatGroups, setIsLoadingThreatGroups] = useState(false);
    const threatGroupRequestId = useRef(0);
    const [localAlerts, setLocalAlerts] = useState<(GeopoliticalEvent | AcapsCrisis)[]>(alerts);
    const [isLoading, setIsLoading] = useState(false);
    const [activeReportAction, setActiveReportAction] = useState<'daily' | 'macro' | 'osint' | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [reportData, setReportData] = useState<any>(null);
    const [filter, setFilter] = useState<'all' | 'high'>('all');
    const [isGenerating, setIsGenerating] = useState(false);
    const [mapFocus, setMapFocus] = useState<{ lat: number; lng: number; zoom?: number } | null>(null);
    
    // 🔍 1. DATA SYNC HOOK: Unconditionally sync localAlerts so initial-empty race is avoided
    useEffect(() => {
        if (riskData.length > 0) {
            console.log("✅ STRATEGIC DATA RECEIVED:", riskData.length, "countries");
        }
        setLocalAlerts(alerts);
    }, [riskData, alerts]);
    
    // 🔍 2. DIAGNOSTIC LOG: Watch the first few records for the 9.3 score
    console.log("🔍 HUB RENDER - Current RiskData Length:", riskData.length);
    if (riskData.length > 0) {
        console.log("📊 SAMPLE DATA (AFG Check):", riskData.find(d => d.iso3 === 'AFG'));
    }

    // 3. Auth Observer
    useEffect(() => {
        if (!auth) return;
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
        });
        return () => unsubscribe();
    }, []);

    // --- 4. MAP INTERACTION (ARMING THE SUITE) ---
    const handleCountryClick = (iso3: string | null) => {
        if (!iso3) return;
        const countryId = iso3.toUpperCase();
        setSelectedCountry(countryId);
        setSelectedThreatGroup('');
        setThreatGroups([]);
        
        // Find the country in your existing riskData array
        const countryInfo = riskData.find(c => c.iso3 === countryId) as any; // ✅ Cast to 'any' to allow .tactical
        
        // ✅ SYNC LOCALLY (No more Cloud Function/CORS errors)
        if (countryInfo?.tactical) {
            setLocalAlerts(countryInfo.tactical);
        } else if (countryInfo?.fullReportData?.tactical) {
            // Check inside fullReportData if that's where it's stored
            setLocalAlerts(countryInfo.fullReportData.tactical);
        }
    
        console.log(`Hub Context Switched: ${countryId}`);
    };

    useEffect(() => {
        const requestId = ++threatGroupRequestId.current;
        setThreatGroups([]);
        setSelectedThreatGroup('');
        if (!selectedCountry) {
            setIsLoadingThreatGroups(false);
            return;
        }
        const countryName = Object.entries(COUNTRY_CODES).find(
            ([, codes]) => codes.iso3 === selectedCountry
        )?.[0] || selectedCountry;
        setIsLoadingThreatGroups(true);
        const search = httpsCallable<{ country: string }, { result: string[] }>(
            getFunctions(undefined, 'us-central1'), 'searchThreatGroupsByCountry', { timeout: 300000 }
        );
        void search({ country: countryName }).then(response => {
            if (requestId === threatGroupRequestId.current)
                setThreatGroups([...new Set((response.data.result || []).filter(group => typeof group === 'string' && group.trim()))]);
        }).catch(error => {
            if (requestId === threatGroupRequestId.current)
                setErrorMessage(error?.message || 'Threat group search failed.');
        }).finally(() => {
            if (requestId === threatGroupRequestId.current)
                setIsLoadingThreatGroups(false);
        });
        return () => { threatGroupRequestId.current++; };
    }, [selectedCountry]);

    // --- 4.5 CONTEXT INITIALIZATION ---
    useEffect(() => {
        if (params?.context === 'ep_planner' && params?.location) {
            console.log("📍 CONTEXT RECEIVED FROM EP PLANNER:", params.projectName);
        }
        
        // SRM Architect Context: Auto-select country from module or project
        if (srmModule?.prefillData?.location_country) {
            handleCountryClick(srmModule.prefillData.location_country);
        } else if (srmProject?.country) {
            handleCountryClick(srmProject.country);
        }

        // SRM Project Location Grounding
        const loc = srmProject?.location || srmModule?.prefillData?.location;
        const lat = srmProject?.lat || loc?.lat;
        const lng = srmProject?.lng || loc?.lng;
        
        if (lat && lng && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
            console.log("IntegratedThreatAnalysis: Grounding map to project coordinates:", lat, lng);
            setMapFocus({ lat: Number(lat), lng: Number(lng), zoom: 14 });
        } else if (srmProject?.address || loc?.address) {
            console.log("IntegratedThreatAnalysis: Project has address but no coordinates. Use handleCountryClick for general area.");
        }

        // LOAD EXISTING RESULTS
        if (srmModule?.resultData) {
            console.log("IntegratedThreatAnalysis: Loading existing resultData");
            setReportData(srmModule.resultData);
            
            // If the result has a country, ensure the map focuses on it
            if (srmModule.resultData.country) {
                setSelectedCountry(srmModule.resultData.country);
            } else if (srmModule.prefillData?.location_country) {
                setSelectedCountry(srmModule.prefillData.location_country);
            }
        }
    }, [params, srmModule, srmProject]);

    // Auto-select country + fire report if arriving via CountryIntelPage deep-link
    useEffect(() => {
        if (params?.preselectedCountry) {
            handleCountryClick(params.preselectedCountry);
        }
    }, [params?.preselectedCountry]);

    useEffect(() => {
        if (!params?.autoRunReport || !params?.preselectedCountry) return;
        // Small delay to ensure selected country state has settled
        const timer = setTimeout(() => {
            if (params.autoRunReport === 'dailyMicro') handleDailyMicro();
            else if (params.autoRunReport === 'macroDeepDive') handleMacroReport();
        }, 300);
        return () => clearTimeout(timer);
    }, [params?.autoRunReport]);



    // --- 5. REPORT GENERATION LOGIC (INTEGRATED SUITE) ---
    
    // Helper to log generated reports
    const saveReportMetadata = async (type: string, country: string, title: string, resultData: any) => {
        if (!auth.currentUser?.email) return;
        try {
            await addDoc(collection(db, "generated_reports"), {
                type,
                country,
                title,
                resultData: sanitizeFirestoreData(resultData),
                timestamp: serverTimestamp(),
                userEmail: auth.currentUser.email,
                userName: auth.currentUser.displayName || auth.currentUser.email,
                // ✅ Agency: Tag client context if present
                clientId: params?.clientId || null,
                organizationId: params?.organizationId || null,
            });
        } catch (e) {
            console.error("Error logging report metadata:", e);
        }
    };

    
    // ✅ Updated PATH 1 in IntegratedThreatAnalysis.tsx
    const handleDailyMicro = async () => {
        if (!selectedCountry) return;
        setIsLoading(true);
        setActiveReportAction('daily');
        try {
            const reportRef = doc(db, "daily_country_analysis", selectedCountry);
            const reportSnapshot = await getDoc(reportRef);
            
            if (!reportSnapshot.exists()) {
                const confirmSync = await confirmApp(
                    `Daily Micro Report for ${selectedCountry} is not pre-cached.\n\n` +
                    `Would you like to trigger a Real-time Intelligence Sync?\n` +
                    `(Estimated time: 45-60 seconds)`
                );
                
                if (confirmSync) {
                    setIsGenerating(true);
                    try {
                        // Use onDemandAnalysisProxy to generate the missing micro report
                        const functions = getFunctions(undefined, 'us-central1');
                        const onDemandAnalysisProxy = httpsCallable(functions, 'onDemandAnalysisProxy');
                        const onDemandResult = await onDemandAnalysisProxy({
                            analysisType: 'Country Micro-Profile',
                            prompt: 'Generate a detailed 24-hour micro tactical intelligence report for this country.',
                            params: { iso3: selectedCountry }
                        });
                        
                        if (onDemandResult.data) {
                            const data = sanitizeFirestoreData(onDemandResult.data);
                            await saveReportMetadata('daily_micro', selectedCountry, `Daily Micro Analysis: ${selectedCountry}`, data);
                            onNavigate({
                                page: 'CountryAnalysisResults', 
                                params: { reportData: data, country: selectedCountry, alerts: newsEvents, isDailyMicro: true }
                            });
                        } else {
                            throw new Error('Intelligence sync failed.');
                        }
                    } catch (err: any) {
                        setErrorMessage(`Sync failed: ${err.message}`);
                    } finally {
                        setIsGenerating(false);
                    }
                }
                return;
            }
            
            const rawData = reportSnapshot.data();
            const data = sanitizeFirestoreData(rawData);
            
            onNavigate({
                page: 'CountryAnalysisResults', 
                params: {
                    reportData: data, 
                    country: selectedCountry,
                    alerts: newsEvents,
                    isDailyMicro: true,
                }
            });
            
            // Log to generated_reports
            await saveReportMetadata('daily_micro', selectedCountry, `Daily Micro Analysis: ${selectedCountry}`, data);

        } catch (e) {
            console.error("Fetch Error:", e);
        } finally {
            setIsLoading(false);
            setActiveReportAction(null);
        }
    };


    // --- 6. TACTICAL DATA FILTERING (Using Centralized Props) ---
    useEffect(() => {
        let filtered = alerts;

        // 1. Filter by Country if selected — check all possible ISO3 field locations
        if (selectedCountry) {
            filtered = alerts.filter(a => {
                const aIso3 = (a as any).iso3
                    || (a as any).geo?.country_iso3
                    || (a as any).location?.country
                    || (a as any).country;
                return normalizeISO3(aIso3) === selectedCountry;
            });
        }
        // 2. Filter by Severity — use flat riskScore (canonical field from risk_events pipeline)
        else if (filter === 'high') {
            filtered = alerts.filter(a => {
                const score = (a as any).riskScore
                    ?? (a as any).analysis?.risk_score
                    ?? (a as any).risk_score
                    ?? 0;
                return score >= 80;
            });
        }

        console.log(`📍 Feed Updated [${filter}]: Rendering ${filtered.length} centralized alerts.`);
        setNewsEvents(filtered as unknown as RiskEvent[]);
    }, [alerts, selectedCountry, filter]);

    // PATH 2: Country Deep Dive — High-Fidelity via triggerManualCountryAnalysis
    // Fetches existing Firestore baseline first, passes it as grounding context to the AI.
    // Uses high-capability routing and the full OSINT stack.
    // Result is automatically persisted to Firestore daily_country_analysis/{ISO3}.
    const handleMacroReport = async () => {
        if (!selectedCountry) return;
        setIsLoading(true);
        setActiveReportAction('macro');
        setErrorMessage(null); // Clear any previous error
        try {
            // Step 1: Fetch existing Firestore daily intelligence as grounding context
            let baseIntelligence: any = null;
            try {
                const dailySnap = await getDoc(doc(db, 'daily_country_analysis', selectedCountry));
                if (dailySnap.exists()) {
                    baseIntelligence = dailySnap.data();
                    console.log('[DeepDive] Loaded Firestore baseline for grounding context.');
                }
            } catch (fetchErr) {
                // Non-fatal: proceed without base intelligence if fetch fails
                console.warn('[DeepDive] Could not fetch Firestore baseline, proceeding without:', fetchErr);
            }

            // Step 2: Call AI with base intelligence as context
            const functions = getFunctions(undefined, 'us-central1');
            const triggerHighFidelity = httpsCallable(
                functions,
                'triggerManualCountryAnalysis',
                { timeout: 540000 }
            );
            
            const response = await triggerHighFidelity({ 
                country: selectedCountry,
                baseIntelligence: baseIntelligence ?? null,
            });
            const responseData = response.data as any;

            if (!responseData?.success) throw new Error('Deep Dive analysis returned no data.');
            const macroData = responseData.data;

            // Log to generated_reports
            await saveReportMetadata('macro_hf', selectedCountry, `Country Deep Dive Analysis: ${selectedCountry}`, macroData);

            onNavigate({
                page: 'CountryAnalysisResults', 
                params: {
                    reportData: macroData, 
                    country: selectedCountry,
                    iso3: responseData.iso3,
                    isDailyMicro: false,
                    activeWorkflowId: 'macroDeepDive',
                    srmProject,
                    srmModule
                }
            });
        } catch (error: any) {
            console.error("Country Deep Dive Error:", error);
            const msg = error?.code === 'functions/deadline-exceeded' || error?.message?.includes('deadline')
                ? 'The analysis timed out — the server is processing large amounts of intelligence data. Please try again in a moment.'
                : `Deep Dive failed: ${error.message || 'Unknown error'}. Please try again.`;
            setErrorMessage(msg);
        } finally {
            setIsLoading(false);
            setActiveReportAction(null);
        }
    };


    // PATH 3: Threat Group OSINT (Tactical Intelligence)
    const handleOSINTReport = async () => {
        if (!selectedCountry || !selectedThreatGroup || !threatGroups.includes(selectedThreatGroup)) return;
        setIsLoading(true);
        setActiveReportAction('osint');
        setErrorMessage(null);
        try {
            const countryName = Object.entries(COUNTRY_CODES).find(
                ([, codes]) => codes.iso3 === selectedCountry
            )?.[0] || selectedCountry;
            const functions = getFunctions(undefined, 'us-central1');
            const analyzeThreatGroup = httpsCallable<
                { groupName: string; country: string; locationContext: { name: string; iso3: string } },
                { result: any }
            >(functions, 'performThreatGroupAnalysis', { timeout: 540000 });
            const result = await analyzeThreatGroup({
                groupName: selectedThreatGroup,
                country: countryName,
                locationContext: {
                    name: countryName,
                    iso3: selectedCountry
                }
            });
            const dossier = result.data.result;
            
            if (dossier) {
                onNavigate({
                    page: 'OSINTDossierResults', 
                    params: {
                        result: {
                            ...dossier,
                            reportType: 'GlobalThreatGroupAnalysis'
                        },
                        country: selectedCountry,
                        srmProject,
                        srmModule
                    }
                });

                // Log to generated_reports
                await saveReportMetadata('osint', selectedCountry, `Threat Group Intelligence: ${selectedThreatGroup}`, dossier);

            }
        } catch (error: any) {
            console.error("OSINT Error:", error);
            setErrorMessage(error?.message || 'OSINT dossier generation failed.');
        } finally { 
            setIsLoading(false); 
            setActiveReportAction(null);
        }
    };

    return (
        <div className="animate-fade-in space-y-6 h-full flex flex-col p-6 bg-slate-50 overflow-y-auto">
            {/* 1. Header - IntelOptics Branding (Cleaned for Copyright) */}
            <div className="flex-shrink-0">
                <button 
                    onClick={onBack} 
                    className="flex items-center text-sm font-medium text-slate-500 hover:text-blue-600 transition-colors mb-4"
                >
                    &larr; Back to Dashboard
                </button>
                <div className="flex justify-between items-end">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">IntelOptics Integrated Threat Analysis Hub</h1>
                        <p className="text-slate-500">Advanced <TerminologyInline term="Geopolitical Risk" onSelect={onOpenGlossary} /> & <TerminologyInline term="Tactical Intelligence" onSelect={onOpenGlossary} /> Monitoring</p>
                    </div>
                    {selectedCountry && (
                        <button 
                            onClick={() => setSelectedCountry(null)}
                            className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200 hover:bg-blue-100 transition-all shadow-sm"
                        >
                            RESET TO GLOBAL VIEW: {selectedCountry} ✕
                        </button>
                    )}
                </div>
            </div>

            {/* Inline Error Banner */}
            {errorMessage && (
                <div className="flex-shrink-0 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-amber-800">Analysis Engine Error</p>
                        <p className="text-sm text-amber-700 mt-0.5">{errorMessage}</p>
                    </div>
                    <button
                        onClick={() => setErrorMessage(null)}
                        className="text-amber-500 hover:text-amber-700 transition-colors text-lg leading-none"
                        aria-label="Dismiss error"
                    >
                        ✕
                    </button>
                </div>
            )}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden min-h-[700px] h-[75vh] flex flex-col relative">
                <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/50 z-10 flex justify-between items-center text-slate-800">
                    <h2 className="text-lg font-semibold tracking-tight"><TerminologyInline term="Global Risk Monitor" label="Global Risk Monitor" onSelect={onOpenGlossary} /></h2>
                    <div className="flex items-center gap-3">
                        {/* --- NEW: High-Risk Toggle Control --- */}
                        <div className="flex bg-slate-100 p-1 rounded-md border border-slate-200">
                            <button 
                                onClick={() => setFilter('all')}
                                className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded transition-all ${
                                    filter === 'all' 
                                    ? 'bg-white text-blue-600 shadow-sm' 
                                    : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                Global Feed
                            </button>
                            <button 
                                onClick={() => setFilter('high')}
                                className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded transition-all ${
                                    filter === 'high' 
                                    ? 'bg-red-50 text-red-600 border border-red-200 shadow-sm' 
                                    : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                Critical Only
                            </button>
                        </div>

                        {/* Updated Status Indicator */}
                        <div className="text-xs text-slate-600 bg-white px-2 py-1 rounded-md border border-slate-200 shadow-sm">
                            <span className={`inline-block w-2 h-2 rounded-full mr-2 animate-pulse ${
                                filter === 'high' ? 'bg-red-500' : 'bg-blue-500'
                            }`}></span>
                            {selectedCountry 
                                ? `Focus: ${selectedCountry}` 
                                : filter === 'high' ? 'Critical-Only Feed' : 'Live Intelligence Feed'
                            }
                        </div>
                                                
                        {/* Add a Reset Button here for easy UX */}
                        {selectedCountry && (
                            <button 
                                onClick={() => {
                                    setSelectedCountry(null);
                                    setLocalAlerts([]); // Clear tactical feed to show global
                                }}
                                className="text-xs bg-white hover:bg-red-50 text-red-600 px-2 py-1 rounded border border-red-200 hover:border-red-300 transition-all shadow-sm"
                            >
                                Reset View
                            </button>
                        )}
                        
                    </div>
                </div>
                <div className="flex-grow relative z-0 w-full h-full">
                    {/* ✅ Map 1: Strategic Colors + Tactical Pins */}
                    <UnifiedMap  
                        isLoaded={isLoaded} 
                        initialViewState={DEFAULT_VIEW_STATE}
                        theme="light"
                        interactive={true}
                        choroplethEnabled={true}
                        riskData={riskData} 
                        alerts={newsEvents.map((e: any) => ({
                            ...e,
                            id: e.id,
                            title: e.title,
                            summary: e.summary || e.description || '',
                            category: e.category || e.type || 'General',
                            sentiment: -1,
                            timestamp: e.processedAt?.toMillis?.() || Date.now(),
                            geo: {
                                lat: e.lat ?? e.latitude ?? e.location?.lat ?? 0,
                                lng: e.lng ?? e.longitude ?? e.location?.lng ?? 0,
                                country_iso3: e.iso3 ?? e.location?.country ?? 'GLB',
                                location_name: e.location?.city ?? e.location_name ?? 'Global'
                            }
                        }))}
                        filterIso3={selectedCountry}
                        focusedLocation={mapFocus}
                        onCountryClick={handleCountryClick} 
                        onNavigate={onNavigate}
                    />

                    {/* RESTORED LEGEND OVERLAY */}
                    <div className="absolute bottom-12 left-6 z-[50] bg-white/95 backdrop-blur-xl p-4 rounded-xl shadow-lg border border-slate-200 text-[11px] text-slate-700 min-w-[140px] pointer-events-auto">
                        <h4 className="font-black mb-3 uppercase tracking-widest text-blue-600 text-[9px]"><TerminologyInline term="Risk Index Matrix" onSelect={onOpenGlossary} /></h4>
                        <div className="grid grid-cols-1 gap-1.5">
                            {/* 8.1+ Critical */}
                            <div className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-[#8B0000] border border-slate-300 mr-2"></span>
                                <span className="w-14 text-slate-600">8.1 +</span> 
                                <span className="font-semibold text-red-600 uppercase text-[9px]"><TerminologyInline term="Critical" onSelect={onOpenGlossary} /></span>
                            </div>
                            {/* 6.1 - 8.0 Very High */}
                            <div className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-[#FF4500] border border-slate-300 mr-2"></span>
                                <span className="w-14 text-slate-600">6.1 - 8.0</span> 
                                <span className="font-semibold text-orange-600">Very High</span>
                            </div>
                            {/* 4.1 - 6.0 High */}
                            <div className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-[#FFA500] border border-slate-300 mr-2"></span>
                                <span className="w-14 text-slate-600">4.1 - 6.0</span> 
                                <span className="font-semibold text-orange-500">High</span>
                            </div>
                            {/* 2.1 - 4.0 Moderate */}
                            <div className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-[#FFFF00] border border-slate-300 mr-2"></span>
                                <span className="w-14 text-slate-600">2.1 - 4.0</span> 
                                <span className="font-semibold text-yellow-600">Moderate</span>
                            </div>
                            {/* 0.0 - 2.0 Low */}
                            <div className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-[#008000] border border-slate-300 mr-2"></span>
                                <span className="w-14 text-slate-600">0.0 - 2.0</span> 
                                <span className="font-semibold text-emerald-600">Low</span>
                                </div>
                            </div>
                    </div>
                </div> {/* 👈 Closes Map/Legend Wrapper (Line 220ish) */}
            </div> {/* 👈 Closes Entire Top Map Section Container */}
    
            {/* 3. Operational Intelligence & Reporting Decks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* DECK 1: ANALYSIS */}
                <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                    <div className="mb-4">
                        <h3 className="text-lg font-bold text-slate-800"><TerminologyInline term="Country Analysis & Strategic Reporting" onSelect={onOpenGlossary} /></h3>
                        <p className="text-sm text-blue-600 font-mono flex items-center mt-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-2"></span>
                            Selected: {selectedCountry || "GLOBAL"}
                        </p>
                    </div>
                    <div className="space-y-3 mt-6">
                        <button 
                            onClick={handleDailyMicro}
                            disabled={!selectedCountry || isLoading}
                            className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-300 text-white font-bold py-3 px-4 rounded-lg shadow-sm shadow-blue-600/30 transition-all flex items-center justify-center space-x-2"
                        >
                            {activeReportAction === 'daily' ? (
                                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                            ) : (
                                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                            )}
                            <span className="truncate">{activeReportAction === 'daily' ? "Fetching Snapshot..." : `View Daily Micro Analysis (${selectedCountry || "..."})`}</span>
                        </button>

                        <button 
                            onClick={handleMacroReport}
                            disabled={!selectedCountry || isLoading}
                            className="w-full bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700 font-bold py-3 px-4 rounded-lg border border-slate-300 transition-all flex items-center justify-center space-x-2"
                        >
                            {activeReportAction === 'macro' ? (
                                <div className="animate-spin h-5 w-5 border-2 border-slate-400 border-t-transparent rounded-full" />
                            ) : (
                                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
                            )}
                            <span className="truncate">{activeReportAction === 'macro' ? "Running High-Fidelity Analysis (45–90s)..." : `Generate Full Intelligence Dossier (${selectedCountry || "..."})`}</span>
                        </button>
                    </div>
                </div>

                {/* DECK 2: THREAT OSINT */}
                <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                    <div className="mb-4">
                        <h3 className="text-lg font-bold text-slate-800">Threat Group <TerminologyInline term="OSINT" onSelect={onOpenGlossary} /> <TerminologyInline term="Tactical Intelligence" onSelect={onOpenGlossary} /></h3>
                        <p className="text-sm text-red-600 font-mono flex items-center mt-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 mr-2 animate-pulse"></span>
                            Region: {selectedCountry || "GLOBAL"}
                        </p>
                    </div>
                    <div className="bg-slate-50 rounded p-4 my-6 border border-slate-200 flex justify-between items-center">
                        <span className="text-xs uppercase text-slate-500 font-bold tracking-widest">Country Threat Groups</span>
                        <p className="text-slate-800 text-lg font-bold">Active Groups: <span className="text-red-600">{isLoadingThreatGroups ? '...' : threatGroups.length}</span></p>
                    </div>
                    <label htmlFor="hub-threat-group" className="block text-sm font-semibold text-slate-700 mb-2">Threat group</label>
                    <select
                        id="hub-threat-group"
                        value={selectedThreatGroup}
                        onChange={event => setSelectedThreatGroup(event.target.value)}
                        disabled={!selectedCountry || isLoadingThreatGroups || isLoading}
                        className="w-full p-3 mb-4 border border-slate-200 rounded-lg bg-slate-50 disabled:text-slate-400"
                    >
                        <option value="">{!selectedCountry ? 'Select a country first...' : isLoadingThreatGroups ? 'Loading threat groups...' : threatGroups.length ? 'Select a threat group...' : 'No threat groups found'}</option>
                        {threatGroups.map(group => <option key={group} value={group}>{group}</option>)}
                    </select>
                    <button 
                        onClick={handleOSINTReport}
                        disabled={!selectedCountry || !selectedThreatGroup || isLoadingThreatGroups || isLoading}
                        className="w-full bg-red-600 hover:bg-red-500 disabled:bg-slate-300 text-white font-bold py-3 px-4 rounded-lg shadow-sm shadow-red-600/30 transition-all flex items-center justify-center space-x-2"
                    >
                        {activeReportAction === 'osint' ? (
                            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                        )}
                        <span className="truncate">{activeReportAction === 'osint' ? "Compiling Dossier..." : `Generate OSINT Dossier (${selectedCountry || "..."})`}</span>
                    </button>
                </div>
            </div>

            {/* Automated Analysis Disclaimer */}
            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start space-x-3">
                <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div className="flex-grow">
                    <p className="text-sm text-blue-800 font-bold">Intelligence Automation Active</p>
                    <p className="text-sm text-blue-700">
                        The background daily analysis will automatically update the active SRM project risk profile it is associated with.
                    </p>
                </div>
            </div>
        </div>
    ); // 👈 Matches return (
}; // 👈 Matches const IntegratedThreatAnalysis = ...

export default IntegratedThreatAnalysis;
